import { SelectOptionType } from "../types";
import { INCIDENT_INFORMATION_CONSTANTS } from "../constants";

/**
 * "Other" referral reason. Always rendered as the last option in any
 * province list to keep ordering predictable for users.
 */
const OTHER_REFERRAL_REASON: SelectOptionType = {
  value: INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS_OTHER_VALUE,
  label: INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS_OTHER_LABEL,
};

/**
 * Helper that constructs an option whose value and label are identical.
 */
const sameValueOption = (text: string): SelectOptionType => ({
  value: text,
  label: text,
});

/**
 * Common "Associate"-style options shared by AB, BC, and the SHARED list
 * (NB, NL, NT, NU, ON, PE, SK, YT). The only difference between the AB/BC
 * variant and the SHARED variant is the second item, which is supplied
 * via {@link buildAssociateReferralReasons}.
 */
const ASSOCIATE_BASE_HEAD: SelectOptionType[] = [sameValueOption("Associate Absent due to injury")];

const ASSOCIATE_BASE_TAIL: SelectOptionType[] = [
  sameValueOption("Associate has requested outside treatment and/or evaluation"),
  sameValueOption(
    "Associate is being referred by AMCARE as they require treatment greater than first aid."
  ),
  sameValueOption("Associate sought treatment after reporting their injury."),
  sameValueOption("Associate sought treatment before reporting their injury (AMCARE Bypass)."),
];

/**
 * Builds the "Associate"-style referral reasons list with a province-specific
 * second item inserted between the shared head and tail. The "Other" option
 * is always appended last.
 */
const buildAssociateReferralReasons = (
  provinceSpecificSecondItem: SelectOptionType
): SelectOptionType[] => [
  ...ASSOCIATE_BASE_HEAD,
  provinceSpecificSecondItem,
  ...ASSOCIATE_BASE_TAIL,
  OTHER_REFERRAL_REASON,
];

/**
 * Alberta (AB) and British Columbia (BC) referral reasons.
 */
const AB_BC_REFERRAL_REASONS: SelectOptionType[] = buildAssociateReferralReasons(
  sameValueOption("Associate has requested OOMD past DOI")
);

/**
 * Shared "Associate"-style referral reasons for NB, NL, NT, NU, ON, PE, SK, YT.
 */
const SHARED_ASSOCIATE_REFERRAL_REASONS: SelectOptionType[] = buildAssociateReferralReasons(
  sameValueOption(
    "Associate has reached 7 calendar days of modified duties and requires referral per WSIB policy"
  )
);

/**
 * "Worker"-style referral reasons shared by Manitoba (MB) and Nova Scotia (NS).
 */
const WORKER_REFERRAL_REASONS: SelectOptionType[] = [
  sameValueOption("Worker has sought medical attention"),
  sameValueOption("Worker is absent due to their injury"),
  sameValueOption(
    "Worker is being referred by Amazon as they require treatment greater than first aid"
  ),
  OTHER_REFERRAL_REASON,
];

/**
 * Quebec (QC) referral reasons.
 */
const QC_REFERRAL_REASONS: SelectOptionType[] = [
  sameValueOption(
    "Worker has provided a certification regarding CNESST Safe Maternity Program (PMSD)"
  ),
  sameValueOption("Worker has provided CNESST medical documents"),
  OTHER_REFERRAL_REASON,
];

/**
 * Province-to-referral-reasons mapping.
 *
 * Lists are grouped by content:
 *   - AB, BC use the AB/BC list ("OOMD past DOI" item)
 *   - NB, NL, NT, NU, ON, PE, SK, YT share the SHARED list ("WSIB policy" item)
 *   - MB, NS share the Worker list
 *   - QC has its CNESST-specific list
 *
 * The "Other" option is always the last entry in each list.
 */
export const SEDGWICK_CANADA_REFERRAL_REASONS_BY_PROVINCE: Record<string, SelectOptionType[]> = {
  AB: AB_BC_REFERRAL_REASONS,
  BC: AB_BC_REFERRAL_REASONS,
  NB: SHARED_ASSOCIATE_REFERRAL_REASONS,
  NL: SHARED_ASSOCIATE_REFERRAL_REASONS,
  NT: SHARED_ASSOCIATE_REFERRAL_REASONS,
  NU: SHARED_ASSOCIATE_REFERRAL_REASONS,
  ON: SHARED_ASSOCIATE_REFERRAL_REASONS,
  PE: SHARED_ASSOCIATE_REFERRAL_REASONS,
  SK: SHARED_ASSOCIATE_REFERRAL_REASONS,
  YT: SHARED_ASSOCIATE_REFERRAL_REASONS,
  MB: WORKER_REFERRAL_REASONS,
  NS: WORKER_REFERRAL_REASONS,
  QC: QC_REFERRAL_REASONS,
};

/**
 * Returns the referral reasons options for a given Canadian province code.
 * Returns an empty array for unknown province codes.
 */
export const getReferralReasonsByProvince = (province: string): SelectOptionType[] => {
  return SEDGWICK_CANADA_REFERRAL_REASONS_BY_PROVINCE[province] || [];
};
